import { istDayKey } from '@wa/shared';

/**
 * The filter panel's model.
 *
 * <b>Two axes, not one list of views.</b> The panel being replicated is four
 * accordion groups — All chats, My chats, SLA breached, Unassigned — each holding
 * the same statuses. That is a scope crossed with a status, and modelling it as a
 * flat list of view names would need twenty names and would make "the open ones in
 * my chats" inexpressible unless somebody had thought to add it.
 *
 * Both axes map onto query parameters the API already indexes and already treats as
 * independent predicates, so nothing is filtered in the browser and the list stays
 * correct when it is longer than one page.
 */
export type InboxScope = 'all' | 'mine' | 'sla' | 'unassigned';
export type InboxStatus = 'open' | 'closed' | 'awaiting' | 'unread' | 'all';

export const SCOPES = ['all', 'mine', 'sla', 'unassigned'] as const satisfies readonly InboxScope[];

/**
 * The groups this person is offered. "All chats" is a supervisor's view and
 * needs `inbox.view_all`; without it the panel starts on My chats. The API
 * narrows every scope to what the person may see regardless, so this is the
 * panel telling the truth about what it would show, not the thing enforcing it.
 */
export function scopesFor(seesAll: boolean): readonly InboxScope[] {
  return seesAll ? SCOPES : SCOPES.filter((scope) => scope !== 'all');
}

/** Where a shift starts: All chats for a supervisor, My chats for everybody else. */
export function homeScope(seesAll: boolean): InboxScope {
  return seesAll ? 'all' : 'mine';
}
export const STATUSES = ['open', 'closed', 'awaiting', 'unread', 'all'] as const satisfies readonly InboxStatus[];

/**
 * Which statuses each group actually offers.
 *
 * `unassigned` on the API means "unassigned **and** not resolved", and
 * `slaBreached` means "not resolved yet". A Closed row under either would be a
 * permanent zero, and a control that can never return anything is worse than a
 * missing one — somebody has to click it to find that out.
 */
export const STATUSES_FOR: Record<InboxScope, readonly InboxStatus[]> = {
  all: STATUSES,
  mine: STATUSES,
  sla: ['open', 'awaiting', 'unread', 'all'],
  unassigned: ['open', 'awaiting', 'unread', 'all'],
};

export const SCOPE_LABEL: Record<InboxScope, string> = {
  all: 'All chats',
  mine: 'My chats',
  sla: 'SLA breached',
  unassigned: 'Unassigned',
};

export const STATUS_LABEL: Record<InboxStatus, string> = {
  open: 'Open',
  closed: 'Closed',
  awaiting: 'Awaiting reply',
  unread: 'Unread',
  all: 'All',
};

/** The View toggle. Groups is built and gated — see the panel. */
export type InboxSurface = 'chats' | 'groups';

/** The named date ranges, in the order the select offers them. */
export type DateRange =
  | 'all' | 'today' | 'yesterday' | 'this_week' | 'last_week'
  | 'this_month' | 'last_month' | 'last_7' | 'last_30' | 'custom';

export const DATE_RANGES: { value: DateRange; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_7', label: 'Last 7 days' },
  { value: 'last_30', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom date range' },
];

export interface InboxFilter {
  scope: InboxScope;
  status: InboxStatus;
  surface: InboxSurface;
  search: string;
  channelId: number | null;
  /** Narrows any group to one tag. */
  tagId: number | null;
  /** A named person, not "me" — what a supervisor filtering their team wants. */
  assignedTo: number | null;
  range: DateRange;
  /** `yyyy-MM-dd`, both inclusive. Set by `range`, or by hand when it is custom. */
  from: string;
  to: string;
}

export const DEFAULT_FILTER: InboxFilter = {
  scope: 'all', status: 'open', surface: 'chats', search: '',
  channelId: null, tagId: null, assignedTo: null,
  range: 'all', from: '', to: '',
};

export function defaultFilterFor(seesAll: boolean): InboxFilter {
  return { ...DEFAULT_FILTER, scope: homeScope(seesAll) };
}

/* ------------------------------------------------------------ date ranges -- */

/*
 * All of this is IST arithmetic and none of it is the browser's.
 *
 * "Today" is an IST calendar day. On a machine set to UTC the browser's own today
 * begins five and a half hours late, so everything an agent sent between midnight
 * and 05:30 would fall out of "Today" — a filter that silently omits the night
 * shift. India has no daylight saving, so a day is always 24 hours and the shifting
 * below is safe; the same code for a zone with DST would not be.
 */

/** Noon, so a ±1 day shift can never cross a boundary into the wrong date. */
const at = (key: string) => new Date(`${key}T12:00:00Z`);
const key = (d: Date) => d.toISOString().slice(0, 10);

const shift = (k: string, days: number) => {
  const d = at(k);
  d.setUTCDate(d.getUTCDate() + days);
  return key(d);
};

/** 0 = Monday. The working week here starts on Monday, not Sunday. */
const weekday = (k: string) => (at(k).getUTCDay() + 6) % 7;

const firstOfMonth = (k: string) => `${k.slice(0, 7)}-01`;

export function rangeToDays(range: DateRange, now: Date = new Date()): { from: string; to: string } {
  const today = istDayKey(now.toISOString());

  switch (range) {
    case 'today': return { from: today, to: today };
    case 'yesterday': return { from: shift(today, -1), to: shift(today, -1) };
    case 'this_week': return { from: shift(today, -weekday(today)), to: today };
    case 'last_week': {
      const monday = shift(today, -weekday(today));
      return { from: shift(monday, -7), to: shift(monday, -1) };
    }
    case 'this_month': return { from: firstOfMonth(today), to: today };
    case 'last_month': {
      const lastDay = shift(firstOfMonth(today), -1);
      return { from: firstOfMonth(lastDay), to: lastDay };
    }
    case 'last_7': return { from: shift(today, -6), to: today };
    case 'last_30': return { from: shift(today, -29), to: today };

    // 'custom' keeps whatever the two inputs hold; 'all' clears both.
    case 'custom': return { from: '', to: '' };
    case 'all': return { from: '', to: '' };
  }
}

/* ------------------------------------------------------------ the request -- */

/**
 * Turns the panel's state into query parameters.
 *
 * Scope and status are set independently, which is the whole point of the two-axis
 * model: `assigned=me&status=open` is one request the API already serves, and no
 * combination needs a name of its own.
 */
export function toQuery(filter: InboxFilter): URLSearchParams {
  const params = new URLSearchParams();

  switch (filter.scope) {
    case 'mine': params.set('assigned', 'me'); break;
    case 'unassigned': params.set('assigned', 'unassigned'); break;
    case 'sla': params.set('slaBreached', 'true'); break;
    case 'all': break;
  }

  switch (filter.status) {
    case 'open': params.set('status', 'open'); break;
    // The API's word for it is `resolved`; the panel's is Closed. One rename, here,
    // rather than a second name for the same state on the server.
    case 'closed': params.set('status', 'resolved'); break;
    case 'awaiting': params.set('status', 'awaiting'); break;
    case 'unread': params.set('unread', 'true'); break;
    case 'all': break;
  }

  if (filter.search.trim()) params.set('q', filter.search.trim());
  if (filter.channelId !== null) params.set('channelId', String(filter.channelId));
  if (filter.tagId !== null) params.set('tag', String(filter.tagId));
  if (filter.assignedTo !== null) params.set('assignedTo', String(filter.assignedTo));
  if (filter.from) params.set('from', filter.from);
  if (filter.to) params.set('to', filter.to);

  return params;
}

/** Nothing narrowed — what the Clear button restores, and when it is offered. */
export function isCleared(f: InboxFilter, seesAll = true): boolean {
  return f.scope === homeScope(seesAll) && f.status === 'open' && f.surface === 'chats'
    && f.tagId === null && f.channelId === null && f.assignedTo === null
    && f.range === 'all' && !f.from && !f.to;
}
